import {inject, Injectable} from '@angular/core';
import {Observable, Observer} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';
import {tap} from 'rxjs/operators';
import {
    AgeRule,
    AllowedValueRule,
    BooleanRule,
    DigitsRule,
    EmailRule,
    IncorrectValueRule,
    MaxLengthRule,
    MaxOrEqualRule,
    MaxRule,
    MinLengthRule,
    MinOrEqualRule,
    MinRule,
    NotEmptyRule,
    PatternRule,
    PhoneRule,
    RequiredRule,
    SizeRule,
    ValidationRuleWithMessage
} from '../rules/rules';
import {ErrorCollection, ErrorCollectionError} from '../util/ErrorCollection';
import {ValidationSubmitEvent} from '../directives/check/validation-check-base.directive';
import {ValidationContext, ValidationContextField} from '../data/validation-context';
import {
    ErrorDetailConstraint,
    isExistingConstraintErrorDetail,
    isModelInvalidParamErrorDetail,
    isModelValidParamErrorDetail,
    isPrimitiveParamErrorDetail,
    isValidationErrorException
} from '~shared/types/error.types';
import {ValidationType} from '~shared/ng/modules/validation/enum/validation-type';
import {ValidationService} from '~shared/ng/modules/validation/services/validation.service';

export interface IValueProvider {
    for($key: string): any;
}

export type ErrorTransformFilter = <T = any>($e: T) => T | null;
export type ErrorHandler = <T = any>($e: T) => T;

/**
 * This service is aimed at monitoring Observables for errors and adding them to ValidationContext.
 */

@Injectable()
export class ErrorObservingService {
    private readonly validationService = inject(ValidationService);

    public static ruleFromConstraintMap = new Map<ValidationType, ($constraint: ErrorDetailConstraint) => ValidationRuleWithMessage>([
        [ValidationType.SIZE, function ($constraint: ErrorDetailConstraint): ValidationRuleWithMessage {
            const minMaxMessage: Array<string> = $constraint.message.split('|');
            return new SizeRule($constraint.min, $constraint.max ?? Number.POSITIVE_INFINITY, minMaxMessage[0], minMaxMessage[1]);
        }],
        [ValidationType.MIN_LENGTH, function ($constraint: ErrorDetailConstraint): ValidationRuleWithMessage {
            return new MinLengthRule($constraint.length, $constraint.message);
        }],
        [ValidationType.MAX_LENGTH, function ($constraint: ErrorDetailConstraint): ValidationRuleWithMessage {
            return new MaxLengthRule($constraint.length, $constraint.message);
        }],
        [ValidationType.MIN, function ($constraint: ErrorDetailConstraint): ValidationRuleWithMessage {
            return new MinRule($constraint.limit, $constraint.message);
        }],
        [ValidationType.MIN_OR_EQUAL, function ($constraint: ErrorDetailConstraint): ValidationRuleWithMessage {
            return new MinOrEqualRule($constraint.limit, $constraint.message);
        }],
        [ValidationType.MAX, function ($constraint: ErrorDetailConstraint): ValidationRuleWithMessage {
            return new MaxRule($constraint.limit, $constraint.message);
        }],
        [ValidationType.MAX_OR_EQUAL, function ($constraint: ErrorDetailConstraint): ValidationRuleWithMessage {
            return new MaxOrEqualRule($constraint.limit, $constraint.message);
        }],
        [ValidationType.REQUIRED, function ($constraint: ErrorDetailConstraint): ValidationRuleWithMessage {
            return new RequiredRule($constraint.message);
        }],
        [ValidationType.NOT_EMPTY, function ($constraint: ErrorDetailConstraint): ValidationRuleWithMessage {
            return new NotEmptyRule($constraint.message);
        }],
        [ValidationType.DIGITS, function ($constraint: ErrorDetailConstraint): ValidationRuleWithMessage {
            return new DigitsRule($constraint.message);
        }],
        [ValidationType.PATTERN, function ($constraint: ErrorDetailConstraint): ValidationRuleWithMessage {
            const regExpSettings: { source: string, flags: string } = JSON.parse($constraint.pattern);
            return new PatternRule(new RegExp(regExpSettings.source, regExpSettings.flags), $constraint.match, $constraint.message);
        }],
        [ValidationType.EMAIL, function ($constraint: ErrorDetailConstraint): ValidationRuleWithMessage {
            return new EmailRule($constraint.strict, $constraint.message);
        }],
        [ValidationType.ALLOWED, function ($constraint: ErrorDetailConstraint): ValidationRuleWithMessage {
            return new AllowedValueRule($constraint.values);
        }],
        [ValidationType.INCORRECT, function ($constraint: ErrorDetailConstraint): ValidationRuleWithMessage {
            return new IncorrectValueRule($constraint.value, $constraint.message);
        }],
        [ValidationType.PHONE, function ($constraint: ErrorDetailConstraint): ValidationRuleWithMessage {
            return new PhoneRule($constraint.message);
        }],
        [ValidationType.AGE, function ($constraint: ErrorDetailConstraint): ValidationRuleWithMessage {
            return new AgeRule($constraint.age, $constraint.message);
        }],
        [ValidationType.BOOLEAN, function ($constraint: ErrorDetailConstraint): ValidationRuleWithMessage {
            return new BooleanRule($constraint.message);
        }],
    ]);

    protected validationErrorHandler($context: ValidationContext, $localContext: ValidationContext, $valueProvider: IValueProvider, $error: any): any {
        if ($error instanceof HttpErrorResponse && isValidationErrorException($error.error)) {
            if (!!$context && !!$localContext) {
                let contextName: string = '';
                for (const detail of $error.error.details) {

                    if (isExistingConstraintErrorDetail(detail)) {
                        continue;
                    }

                    if (isPrimitiveParamErrorDetail(detail)) {
                        // There are no rule for this error, it should be displayed without creating constraint.
                        const rule = new IncorrectValueRule($valueProvider.for(detail.paramName), detail.errorMessage);
                        this.writeFieldIntoContext(detail.paramName, rule, $localContext);
                    }

                    if (isModelInvalidParamErrorDetail(detail) || isModelValidParamErrorDetail(detail)) {
                        if (!contextName) {
                            contextName = detail.constraint.context;
                        }

                        // create rules by constraint
                        const createRuleFn = ErrorObservingService.ruleFromConstraintMap.get(detail.constraint.type);

                        if (!createRuleFn) {
                            console.warn(`Unknown constraint received from server, plz add it to the list.`);
                        } else {
                            const rule = createRuleFn(detail.constraint);
                            this.writeFieldIntoContext(detail.key, rule, $localContext);
                        }
                    } else {
                        console.warn(`Unknown validation error data received from server, plz add it to the list.`);
                    }
                }
                this.validationService.changeContext(contextName, $localContext);
            }
        }

        return $error;
    }

    protected writeFieldIntoContext($key: string, $rule: ValidationRuleWithMessage, $context: ValidationContext): void {
        const fieldData = $context.getFieldSnapshot($key);

        if (fieldData) {
            const rulesSnapshot = fieldData.getRulesSnapshot();
            const existingRuleIndex = rulesSnapshot.findIndex($ => $.name === $rule.name);
            if (existingRuleIndex > -1) {
                fieldData.swapRules(
                    rulesSnapshot
                        .slice(0, existingRuleIndex)
                        .concat(
                            [$rule, ...rulesSnapshot.slice(existingRuleIndex + 1)]
                        )
                );
            } else {
                fieldData.addRule($rule);
            }
            // Currently this is not required, but intended by design.
            $context.changeField($key, fieldData);
        } else {
            const newFieldContext = new ValidationContextField($key);
            newFieldContext.addRule($rule);
            $context.changeField($key, newFieldContext);
        }
    }

    protected nonValidationErrorHandler($errorCollection: ErrorCollection, $error: any): any {
        if ($error instanceof HttpErrorResponse && !isValidationErrorException($error.error)) {
            if ($errorCollection) {
                if (typeof $error.error === 'string') {
                    $errorCollection.addError(new ErrorCollectionError($error.error));
                } else {
                    $errorCollection.addError(new ErrorCollectionError($error.error.message || $error.message));
                }
            }
        }
        return $error;
    }

    protected errorTransformFilter<T = any>($cb: ErrorTransformFilter | null, $error: T): T | null {
        if ($cb) {
            // if ($error instanceof HttpErrorResponse && !!$error.error) {
            //     return $cb($error.error);
            // }
            return $cb($error);
        }
        return $error;
    }

    protected definedErrorHandler(
        $context: ValidationContext,
        $localContext: ValidationContext,
        $errorCollection: ErrorCollection,
        $valueProvider: IValueProvider,
        $transformFilter: ErrorTransformFilter,
        $error: any
    ): any {
        if ($error instanceof HttpErrorResponse && !!$error.error) {
            return this.validationErrorHandler(
                $context,
                $localContext,
                $valueProvider,
                this.nonValidationErrorHandler(
                    $errorCollection,
                    this.errorTransformFilter($transformFilter, $error)
                )
            );
        }

        return $error;
    }

    protected undefinedErrorHandler($errorCollection: ErrorCollection, $error: any): any {
        if ($error instanceof HttpErrorResponse && !$error.error) {
            if ($error.hasOwnProperty('message')) {
                if ($errorCollection) {
                    $errorCollection.addError(new ErrorCollectionError($error.message));
                }
            }
        }

        return $error;
    }

    protected httpErrorHandler(
        $context: ValidationContext,
        $localContext: ValidationContext,
        $errorCollection: ErrorCollection,
        $valueProvider: IValueProvider,
        $filterOut: ErrorTransformFilter,
        $error: any
    ): any {
        if ($error instanceof HttpErrorResponse) {
            return this.undefinedErrorHandler(
                $errorCollection,
                this.definedErrorHandler($context, $localContext, $errorCollection, $valueProvider, $filterOut, $error),
            );
        }

        return $error;
    }

    protected nonHttpErrorHandler($errorCollection: ErrorCollection, $error: any): any {
        if (!($error instanceof HttpErrorResponse)) {
            // FIXME: This could also have validation error!!!
            if ($error.hasOwnProperty('message')) {
                if ($errorCollection) {
                    $errorCollection.addError(new ErrorCollectionError(
                        $error.message
                    ));
                }
            }
        }


        return $error;
    }

    protected createObserver<T>(
        $context: ValidationContext,
        $localContext: ValidationContext,
        $errorCollection: ErrorCollection,
        $valueProvider?: IValueProvider,
        $filterOut?: ErrorTransformFilter
    ): Observer<T> {
        const clearErrorCollection: ErrorHandler = <E extends any>($error: E): E => {
            if ($errorCollection) {
                $errorCollection.clear();
            }

            return $error;
        };

        return {
            next: () => {
            },
            error: ($error) => {
                return this.httpErrorHandler(
                    $context,
                    $localContext,
                    $errorCollection,
                    $valueProvider,
                    $filterOut,
                    this.nonHttpErrorHandler(
                        $errorCollection,
                        clearErrorCollection($error)
                    ),
                );
            },
            complete: () => {
            },
        };

    }

    public observeOn<T>($event: ValidationSubmitEvent, $filterOut?: ErrorTransformFilter): Observer<T> {
        return this.createObserver<T>(
            $event.context,
            $event.localContext,
            $event.errorCollection,
            {for: ($key) => $event.formData[$key]},
            $filterOut
        );
    }

    public observeTo<T>($errorCollection: ErrorCollection, $filterOut?: ErrorTransformFilter): Observer<T> {
        return this.createObserver<T>(
            undefined,
            undefined,
            $errorCollection,
            undefined,
            $filterOut
        );
    }

    public observe<T = any>($target: Observable<T>, $context: ValidationContext, $localContext: ValidationContext, $errorCollection: ErrorCollection): Observable<T> {
        return $target
            .pipe(
                tap(this.createObserver<T>($context, $localContext, $errorCollection)),
            );
    }
}
